import { z } from 'zod';
import { AutonomyClass, SideEffectClass } from './actions.ts';
import { JsonSchemaObject } from './common.ts';
import { ToolOperationDeclaration } from './operation.ts';

/** A tool exposed to agents, as handed to a model: name, description and JSON Schema input. */
export const ToolSpec = z
  .object({
    name: z.string().min(1),
    description: z.string(),
    inputSchema: JsonSchemaObject,
    version: z.string().min(1),
  })
  .meta({ id: 'ToolSpec' });
export type ToolSpec = z.infer<typeof ToolSpec>;

/** Catalog entry for a business tool. The tool gateway enforces every field. */
export const ToolCatalogEntry = z
  .object({
    /** Dotted name, e.g. `supplier.create`. */
    name: z.string().regex(/^[a-z0-9][a-z0-9_.-]*$/, 'tool name must be lowercase dotted'),
    version: z.string().min(1),
    owner: z.string().min(1),
    purpose: z.string().min(1),
    disallowedUse: z.array(z.string()).default([]),
    inputSchema: JsonSchemaObject,
    outputSchema: JsonSchemaObject,
    requiredScopes: z.array(z.string()).default([]),
    /** Target type the tool acts on, e.g. `erp.supplier` (used by policy `targetTypes`). */
    targetType: z.string().optional(),
    /** Free-text or rule ids describing resource constraints. */
    resourceRules: z.array(z.string()).default([]),
    sideEffectClass: SideEffectClass,
    autonomyClass: AutonomyClass,
    approvalPolicy: z.enum(['none', 'required', 'policy']),
    idempotency: z.enum(['natural', 'key', 'none']),
    dryRun: z.boolean().default(false),
    expectedErrors: z.array(z.string()).default([]),
    timeoutMs: z.number().int().positive().default(10_000),
    /** `safe` may be retried blindly, `with_key` only with the same idempotency key, `never` must reconcile. */
    retryClass: z.enum(['safe', 'with_key', 'never']),
    maxResultBytes: z.number().int().positive().default(65_536),
    /**
     * Added by 17a: executions are business operations in this namespace; the handler-side deriver
     * (`ToolImplementation.operation`) supplies key and payload hash. Absent = no business operation.
     */
    operation: ToolOperationDeclaration.optional(),
    provenance: z.looseObject({
      /** Module (pack) that registered the tool. */
      module: z.string().min(1),
      kind: z.enum(['native', 'mcp', 'http']).default('native'),
      ref: z.string().optional(),
    }),
  })
  .meta({ id: 'ToolCatalogEntry' });
export type ToolCatalogEntry = z.infer<typeof ToolCatalogEntry>;
export type ToolCatalogEntryInput = z.input<typeof ToolCatalogEntry>;
